/**
 * ============================================================================
 * eDMS Web — Router (TanStack Router v1)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: Phase 1 closure — wires every shipped screen at its canonical
 *          URL so the e2e suite (tests/e2e/01..05) can reach each flow
 *          without `test.fixme` guards.
 *
 * Route tree:
 *   /                                    → AuthorWorkbench (default landing)
 *   /login                               → LoginScreen
 *   /auth/callback                       → AuthCallback (OIDC PKCE return)
 *   /workflows/:id                       → ReviewerView (diff + sign REVIEW)
 *   /qa                                  → QAWorkbench
 *   /r-and-u/:versionId                  → RUReader (scroll-gate + quiz + sign R&U)
 *   /inspector/audit                     → InspectorMode (read-only, AI-free)
 *   /2fa/enroll                          → TwoFactorEnrollment
 *   /notifications, /admin/*,
 *   /training, /ocr/review               → Phase 3 + 4 + 4.5 screens
 *   /retention/matrix, /retention/disposition
 *                                        → Phase 6a — Retention
 *   /quality-metrics, .../thresholds,
 *   .../snapshots                        → Phase 6b — Quality Metrics v2
 *   /changes/new, /changes/:id/impact-set,
 *   /changes/:id/post-actions            → Phase 6c — CC Impact Engine
 *   /suppliers, /suppliers/:id,
 *   /suppliers/risk-ranking,
 *   /suppliers/kpi-dashboard,
 *   /vendor-evaluations/:id              → Phase 6d — Supplier Management
 *   /equipment, /equipment/:id,
 *   /equipment/calibrations, .../pm,
 *   .../requalification-schedule         → Phase 6e — Equipment & Validation
 *
 * Auth model:
 *   - The root layout reads sessionStorage via useSession() and redirects
 *     anonymous users to /login (preserving returnTo via query string).
 *   - /login + /auth/callback + /2fa/enroll are PUBLIC routes.
 *   - The 5 inner screens require an authenticated session.
 *   - Inspector + QA + R&U are role-gated at the route component level
 *     (the AppShell still renders for unauthorised users with a friendly
 *     403 panel, which keeps the audit trail clean — INSP-002 + RBAC-007).
 *
 * URS rows: AUTH-001/008 (session enforcement), INSP-001/002 (banner persists),
 *           AI-009 (inspector mode is AI-free), I18N-002 (RTL retained on route
 *           change because <html dir> is set by i18next, not the router).
 * ============================================================================
 */

import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  useParams,
} from '@tanstack/react-router';

import { AppShell } from './components/AppShell/AppShell';
import { SessionProvider, useSession } from './auth/SessionProvider';
import { HomeDashboard } from './screens/Home';
import { AuthorWorkbench } from './screens/AuthorWorkbench/AuthorWorkbench';
import { LoginScreen } from './screens/LoginScreen/LoginScreen';
import { AuthCallback } from './screens/AuthCallback/AuthCallback';
import { ReviewerView } from './screens/ReviewerView/ReviewerView';
import { QAWorkbench } from './screens/QAWorkbench/QAWorkbench';
import { RUReader } from './screens/RUReader/RUReader';
import { InspectorMode } from './screens/InspectorMode/InspectorMode';
import { TwoFactorEnrollment } from './screens/TwoFactorEnrollment/TwoFactorEnrollment';
// Phase 3 admin screens
import { NotificationInbox } from './screens/NotificationInbox/NotificationInbox';
import { WebhookAdmin } from './screens/WebhookAdmin/WebhookAdmin';
import { TenantConfigAdmin } from './screens/TenantConfigAdmin/TenantConfigAdmin';
import { DocumentNumberingAdmin } from './screens/DocumentNumberingAdmin';
import { DataScenarios } from './screens/DataScenarios';
import { BmrReview } from './screens/BmrReview';
// V18 — Department hierarchy + SOP template library
import { DepartmentAdmin } from './screens/DepartmentAdmin';
import { TemplateLibrary } from './screens/TemplateLibrary';
// V26 — Multi-site registry
import { SitesAdmin } from './screens/SitesAdmin';
// drafts/53 §A — QA Director Dashboard
import { QualityDashboard } from './screens/QualityDashboard';
// drafts/53 §C — In-app DOCX editor (OnlyOffice)
import { DocumentEditor } from './screens/DocumentEditor';
// Phase 4 training screen
import { TrainingTasks } from './screens/TrainingTasks/TrainingTasks';
// Phase 4.5 OCR review queue (Doc Controller)
import { OcrReviewQueue } from './screens/OcrReviewQueue/OcrReviewQueue';
// Phase 6a — Retention
import { RetentionMatrixAdmin } from './screens/RetentionMatrixAdmin';
import { RecordsDispositionTray } from './screens/RecordsDispositionTray';
// Phase 6b — Quality Metrics v2
import { QualityMetricsDashboard } from './screens/QualityMetricsDashboard';
import { ThresholdMatrixAdmin } from './screens/ThresholdMatrixAdmin';
import { SnapshotSigningTray } from './screens/SnapshotSigningTray';
// Phase 6c — CC Impact Engine
import { ChangeRequestProposal } from './screens/ChangeRequestProposal';
import { ImpactSetReview } from './screens/ImpactSetReview';
import { PostActionTracker } from './screens/PostActionTracker';
// Legacy CC Impact Engine register (route /changes). Aliased so it does not
// collide with the new change_control register imported below.
import { ChangeControlRegister as LegacyChangeRegister } from './screens/ChangeControlRegister';
// Phase 6d — Supplier Management
import { SupplierWorkbench } from './screens/SupplierWorkbench';
import { SupplierDetail } from './screens/SupplierDetail';
import { RiskRankingDashboard } from './screens/RiskRankingDashboard';
import { SupplierKpiDashboard } from './screens/SupplierKpiDashboard';
import { VendorEvaluationScorecard } from './screens/VendorEvaluationScorecard';
// Phase 6e — Equipment & Validation
import { EquipmentWorkbench } from './screens/EquipmentWorkbench';
import { EquipmentDetail } from './screens/EquipmentDetail';
import { CalibrationTray } from './screens/CalibrationTray';
import { PMTray } from './screens/PMTray';
import { RequalificationDashboard } from './screens/RequalificationDashboard';
// Phase 7 — Full eQMS modules
import { CapaWorkbench, CapaDetail } from './screens/CapaWorkbench';
import { DeviationTriage } from './screens/DeviationTriage';
import { DeviationDetail } from './screens/DeviationDetail';
import { OosLog } from './screens/OosLog';
import { OosInvestigation } from './screens/OosInvestigation';
import { RiskRegister } from './screens/RiskRegister';
import { RiskEntry } from './screens/RiskEntry';
import { ComplaintsRegister, ComplaintDetail } from './screens/Complaints';
import { RecallsRegister, RecallDetail } from './screens/Recalls';
import { ChangeControlRegister } from './screens/ChangeControl/ChangeControlRegister';
import { ChangeControlDetail } from './screens/ChangeControl/ChangeControlDetail';
import { ChangeControlDashboard } from './screens/ChangeControl/ChangeControlDashboard';
import { ProductionMaster } from './screens/Production/ProductionMaster';
import { gated } from './components/RequireRoles/RequireRoles';
import { PharmacovigilanceRegister } from './screens/Pharmacovigilance/PharmacovigilanceRegister';
import { ValidationRegister, ValidationDetail } from './screens/Validation';
import { ScarRegister, ScarDetail } from './screens/Scar';
import { RaDashboard, ProductRegister, ProductDetail, RegistrationTracker, RegistrationDetail, VariationManagement, VariationDetail, RenewalsTracker, CountryMatrix, CommitmentsTracker, CommitmentDetail } from './screens/Regulatory';
import { ActionCenter } from './screens/ActionCenter';
import { AuditCalendar } from './screens/AuditCalendar';
import { AuditDetail } from './screens/AuditDetail';
import { ReviewCalendar } from './screens/ReviewCalendar';
import { ReviewMinutes } from './screens/ReviewMinutes';
// =============================================================================
// W4.1 (2026-05-28) — Lazy-load every Finance route.
//
// Why lazy: bundle was 1,428 kB (387 kB gzipped) and Vite warned every build.
// Most users aren't in the Finance module on first paint, so pulling the PAF+
// charts, the pdf-lib renderer-supporting dialog, the 8 admin dialogs, etc.
// up-front is pure waste. Each `lazy(import('./…'))` becomes its own chunk
// that Vite/rollup ships only when the route is hit.
//
// Why NOT vite.config `manualChunks`: previously tried and reverted (see
// `vite.config.ts` comment) — produced a circular chunk graph that made the
// page blank-load on Hostinger. Route-level lazy bypasses that risk because
// rollup builds the dependency graph itself from real imports.
//
// `Suspense` fallback is a small spinner — sub-100 ms on broadband; the
// chunk caches after first hit so subsequent navigations are instant.
// =============================================================================
import { lazy, Suspense } from 'react';

// Inline barebones fallback. Avoids importing the design-system Spinner so
// the lazy boundary stays free of design-system code.
function LazyFinanceFallback() {
  return (
    <div style={{ padding: 32, color: '#6B7989', fontSize: 14 }} role="status" aria-live="polite">
      Loading…
    </div>
  );
}

// Helper so the per-route boilerplate stays terse + the chunk-name comment
// is colocated with the import (Vite picks `chunkName` from the comment).
// Return type is intentionally inferred (`() => JSX.Element`) rather than
// annotated `React.ComponentType`: the latter is a `ComponentClass | FunctionComponent`
// union that TanStack Router's `RouteComponent` (a callable with an optional
// `preload`) rejects, while the inferred function-component type is assignable.
// When a deploy ships new chunk hashes, an already-open SPA tab still holds the
// OLD index.html in memory; navigating to a lazy route then tries to import a
// now-gone chunk and throws "Failed to fetch dynamically imported module"
// (the server returns index.html for the missing asset). Reload ONCE to pull
// the fresh, no-cache index.html + new chunk map. The sessionStorage guard
// stops a reload loop when the failure is genuine (offline, real 5xx).
const CHUNK_RELOAD_KEY = 'edms:chunk-reloaded';
// Only a missing/failed chunk fetch should trigger a reload — NOT a genuine
// runtime error thrown from inside a module that loaded fine. Matches the
// browser/Vite messages for a failed dynamic import / module-preload.
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|Importing a module script failed/i.test(msg);
}
export async function importWithReload<T>(loader: () => Promise<T>): Promise<T> {
  try {
    const mod = await loader();
    sessionStorage.removeItem(CHUNK_RELOAD_KEY); // success → a later deploy may reload again
    return mod;
  } catch (err) {
    // Re-throw anything that isn't a stale-chunk fetch failure (real module bugs
    // must surface, not be masked by a reload).
    if (isChunkLoadError(err) && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
      window.location.reload();
      return new Promise<T>(() => {}); // never resolves — the page is reloading
    }
    throw err; // not a chunk error, or already reloaded once this session
  }
}

function lazyFinance<T extends Record<string, React.ComponentType<unknown>>>(
  loader: () => Promise<T>,
  pick: keyof T,
) {
  const Lazy = lazy(async () => {
    const mod = await importWithReload(loader);
    return { default: mod[pick]! as React.ComponentType };
  });
  return function LazyFinanceRoute() {
    return (
      <Suspense fallback={<LazyFinanceFallback />}>
        <Lazy />
      </Suspense>
    );
  };
}

// v0.13 — Finance module (Tier 1)
const CostOfQualityDashboard = lazyFinance(
  () => import(/* webpackChunkName: "finance-coq" */ './screens/Finance/CostOfQualityDashboard/CostOfQualityDashboard'),
  'CostOfQualityDashboard',
);
const CostEntriesPage = lazyFinance(
  () => import(/* webpackChunkName: "finance-entries" */ './screens/Finance/CostAttribution/CostEntriesPage'),
  'CostEntriesPage',
);
const QualityFinanceCockpit = lazyFinance(
  () => import(/* webpackChunkName: "finance-cockpit" */ './screens/Finance/QualityFinanceCockpit/QualityFinanceCockpit'),
  'QualityFinanceCockpit',
);
// Finance Statement-Intelligence — Executive dashboard (FIN-P1-5)
const ExecutiveDashboard = lazyFinance(
  () => import(/* webpackChunkName: "finance-executive" */ './screens/Finance/ExecutiveDashboard/ExecutiveDashboard'),
  'ExecutiveDashboard',
);
// FIN-7 — Working-Capital Center
const WorkingCapitalCenter = lazyFinance(
  () => import(/* webpackChunkName: "finance-working-capital" */ './screens/Finance/WorkingCapitalCenter/WorkingCapitalCenter'),
  'WorkingCapitalCenter',
);
// FIN-8 — Treasury Center
const TreasuryCenter = lazyFinance(
  () => import(/* webpackChunkName: "finance-treasury" */ './screens/Finance/TreasuryCenter/TreasuryCenter'),
  'TreasuryCenter',
);
// FIN-9 — Profitability Center
const ProfitabilityCenter = lazyFinance(
  () => import(/* webpackChunkName: "finance-profitability" */ './screens/Finance/ProfitabilityCenter/ProfitabilityCenter'),
  'ProfitabilityCenter',
);
// FIN-13 — Pharma KPI Center
const PharmaKpiCenter = lazyFinance(
  () => import(/* webpackChunkName: "finance-pharma-kpis" */ './screens/Finance/PharmaKpiCenter/PharmaKpiCenter'),
  'PharmaKpiCenter',
);
// FIN-12 — CEO / Board MTD + variance
const BoardMetrics = lazyFinance(
  () => import(/* webpackChunkName: "finance-board-metrics" */ './screens/Finance/BoardMetrics/BoardMetrics'),
  'BoardMetrics',
);
// FIN-10 — Forecasting Center
const ForecastingCenter = lazyFinance(
  () => import(/* webpackChunkName: "finance-forecasting" */ './screens/Finance/ForecastingCenter/ForecastingCenter'),
  'ForecastingCenter',
);
// SAP statement upload pipeline (FIN-P1-6)
const FinanceUploads = lazyFinance(
  () => import(/* webpackChunkName: "finance-uploads" */ './screens/Finance/Uploads/Uploads'),
  'FinanceUploads',
);
const ExposureRegister = lazyFinance(
  () => import(/* webpackChunkName: "finance-exposure" */ './screens/Finance/ExposureRegister/ExposureRegister'),
  'ExposureRegister',
);
const BoardPack = lazyFinance(
  () => import(/* webpackChunkName: "finance-board-pack" */ './screens/Finance/BoardPack/BoardPack'),
  'BoardPack',
);
const CostRatesAdmin = lazyFinance(
  () => import(/* webpackChunkName: "finance-admin-rates" */ './screens/Finance/admin/CostRatesAdmin'),
  'CostRatesAdmin',
);
const PeriodLocksAdmin = lazyFinance(
  () => import(/* webpackChunkName: "finance-admin-locks" */ './screens/Finance/admin/PeriodLocksAdmin'),
  'PeriodLocksAdmin',
);
// v0.13.2 — Finance Tier 2 (Budgets + Variance)
const Budgets = lazyFinance(
  () => import(/* webpackChunkName: "finance-budgets" */ './screens/Finance/Budgets/Budgets'),
  'Budgets',
);
const Variance = lazyFinance(
  () => import(/* webpackChunkName: "finance-variance" */ './screens/Finance/Variance/Variance'),
  'Variance',
);
// v0.13.3 — Finance Tier 3 (CAPEX)
const CapexPortfolio = lazyFinance(
  () => import(/* webpackChunkName: "finance-capex" */ './screens/Finance/CapexPortfolio/CapexPortfolio'),
  'CapexPortfolio',
);
const CapexDetail = lazyFinance(
  () => import(/* webpackChunkName: "finance-capex-detail" */ './screens/Finance/CapexPortfolio/CapexDetail'),
  'CapexDetail',
);
// Finance Phase 1a — CAPA ROI ranking
const CapaRoiRanking = lazyFinance(
  () => import(/* webpackChunkName: "finance-capa-roi" */ './screens/Finance/CapaRoiRanking/CapaRoiRanking'),
  'CapaRoiRanking',
);
// Finance F4 — automated cost capture
const CostCapture = lazyFinance(
  () => import(/* webpackChunkName: "finance-cost-capture" */ './screens/Finance/CostCapture/CostCapture'),
  'CostCapture',
);
// Finance F5 — ERP write-back + period close
const PeriodClose = lazyFinance(
  () => import(/* webpackChunkName: "finance-period-close" */ './screens/Finance/PeriodClose/PeriodClose'),
  'PeriodClose',
);
// Finance F6 — board-grade analytics
const BoardAnalytics = lazyFinance(
  () => import(/* webpackChunkName: "finance-board-analytics" */ './screens/Finance/BoardAnalytics/BoardAnalytics'),
  'BoardAnalytics',
);
// v0.13.4 — Finance Tier 4a (ERP read-only)
const ErpReconciliation = lazyFinance(
  () => import(/* webpackChunkName: "finance-erp-recon" */ './screens/Finance/ErpReconciliation/ErpReconciliation'),
  'ErpReconciliation',
);
const ErpAdmin = lazyFinance(
  () => import(/* webpackChunkName: "finance-admin-erp" */ './screens/Finance/admin/ErpAdmin'),
  'ErpAdmin',
);
// W5.4 — Finance-scoped audit-trail viewer. Filters audit.system_audit_log
// to FINANCE_* event_kinds (13 kinds as of W5.3). Roughly a CFO/auditor
// surface — no write actions, just read + filter + JSON payload expand.
const FinanceAuditViewer = lazyFinance(
  () => import(/* webpackChunkName: "finance-admin-audit" */ './screens/Finance/admin/FinanceAuditViewer'),
  'FinanceAuditViewer',
);

// ----- Root layout (always rendered) -----
const rootRoute = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  const { t } = useTranslation();
  return (
    <SessionProvider>
      <AppShell title={t('app.name')}>
        <main id="main" tabIndex={-1}>
          <Outlet />
        </main>
      </AppShell>
    </SessionProvider>
  );
}

// ----- Public routes -----
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginScreen,
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallback,
});

const twoFactorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/2fa/enroll',
  component: TwoFactorEnrollment,
});

// ----- Auth-required routes -----
// All inner routes share an auth guard that redirects anonymous users to /login.
const authedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '__authed',
  component: AuthedOutlet,
  beforeLoad: ({ location }) => {
    // Reading sessionStorage directly because beforeLoad runs OUTSIDE React;
    // SessionProvider is the render-time source of truth, this is the routing
    // guard. The keys mirror oidcClient.ts STORAGE_KEYS.
    if (typeof window === 'undefined') return;
    const access = window.sessionStorage.getItem('edms.session.access');
    const expiresStr = window.sessionStorage.getItem('edms.session.expires');
    const expired = expiresStr ? Number(expiresStr) < Date.now() : true;
    if (!access || expired) {
      throw redirect({
        to: '/login',
        search: { returnTo: location.pathname + (location.searchStr ?? '') },
      });
    }
  },
});

function AuthedOutlet(): ReactNode {
  return <Outlet />;
}

const homeRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/',
  component: HomeDashboard,
});
const actionCenterRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/action-center',
  component: ActionCenter,
});

const workbenchRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/workbench',
  component: AuthorWorkbench,
});

// Pass the $id route param through (ReviewerView otherwise falls back to its
// 'wf-abc-123' default, so every workflow URL shared one record). Mirrors
// SupplierDetailRoute / DocumentDetailRoute.
function WorkflowReviewRoute() {
  const { id } = useParams({ strict: false }) as { id: string };
  return <ReviewerView workflowId={id} />;
}
const workflowDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/workflows/$id',
  component: WorkflowReviewRoute,
});

// Param-passing wrappers for the QMS detail screens. TanStack Router does not
// inject route params as props, so without these the screen loads with an
// undefined id (→ "Could not load", a hardcoded demo id, or /endpoint/undefined).
// Mirrors WorkflowReviewRoute / SupplierDetailRoute. Function declarations are
// hoisted, so these can sit here and be referenced by routes declared below.
function CapaDetailRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <CapaDetail capaId={id} />; }
function DeviationDetailRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <DeviationDetail deviationId={id} />; }
function OosDetailRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <OosInvestigation oosId={id} />; }
function RiskEntryRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <RiskEntry riskId={id} />; }
function ComplaintDetailRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <ComplaintDetail complaintId={id} />; }
function RecallDetailRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <RecallDetail recallId={id} />; }
function ValidationDetailRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <ValidationDetail validationId={id} />; }
function ScarDetailRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <ScarDetail scarId={id} />; }
function ProductDetailRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <ProductDetail productId={id} />; }
function RegistrationDetailRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <RegistrationDetail registrationId={id} />; }
function VariationDetailRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <VariationDetail variationId={id} />; }
function AuditDetailRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <AuditDetail auditId={id} />; }
function ReviewMinutesRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <ReviewMinutes reviewId={id} />; }
function EquipmentDetailRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <EquipmentDetail equipmentId={id} />; }
function ChangeControlDetailRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <ChangeControlDetail id={id} />; }

const qaRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/qa',
  component: QAWorkbench,
});

const ruReaderRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/r-and-u/$versionId',
  component: RUReader,
});

const inspectorRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/inspector/audit',
  component: InspectorMode,
});

// Phase 3 admin screens (Phase 3 closure 2026-05-18).
const notificationsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/notifications',
  component: NotificationInbox,
});

const webhookAdminRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/admin/webhooks',
  component: WebhookAdmin,
});

const tenantConfigAdminRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/admin/tenant-config',
  component: TenantConfigAdmin,
});

// C-12 — configurable document numbering schemes
const documentNumberingRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/admin/numbering',
  component: DocumentNumberingAdmin,
});

// DDM-008 — Demo Data Manager admin screen
const dataScenariosRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/admin/data-scenarios',
  component: DataScenarios,
});

// AI-308 — BMR Review Accelerator detail
const bmrReviewRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/quality/bmr-review/$id',
  component: BmrReview,
});

// v0.12.9 — parent-path redirects so breadcrumb clicks on /quality and
// /quality/bmr-review don't render "Not Found".
const qualityIndexRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/quality',
  beforeLoad: () => { throw redirect({ to: '/dashboard/quality' }); },
  component: () => null,
});
const bmrReviewIndexRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/quality/bmr-review',
  beforeLoad: () => { throw redirect({ to: '/dashboard/quality' }); },
  component: () => null,
});

// V18 — Department hierarchy admin (org chart + position holders)
const departmentAdminRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/admin/departments',
  component: DepartmentAdmin,
});

// V26 — Multi-site registry (Riyadh / Jeddah / Dammam + authorization matrix)
const sitesAdminRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/admin/sites',
  component: SitesAdmin,
});

// V18 — SOP template library
const templateLibraryRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/templates',
  component: TemplateLibrary,
});

// drafts/53 §A — QA Director Dashboard (Quality KPIs + alerts + CoQ + AI chip)
const qualityDashboardRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/dashboard/quality',
  component: QualityDashboard,
});

// v0.12.3 — Document detail page (/documents/$id)
import { DocumentDetail } from './screens/DocumentDetail';
function DocumentDetailRoute() {
  return <DocumentDetail />;
}
const documentDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/documents/$id',
  component: DocumentDetailRoute,
});
// eDMS E6 — full-text + faceted document search (/documents/search)
import { DocumentSearch } from './screens/DocumentSearch';
const documentSearchRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/documents/search',
  component: DocumentSearch,
});
// C-13 — OCR full-text search (/documents/ocr-search)
import { OcrSearch } from './screens/OcrSearch';
const ocrSearchRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/documents/ocr-search',
  component: OcrSearch,
});
// C-14 — corporate / non-GMP document categories (/documents/corporate)
import { CorporateDocuments } from './screens/CorporateDocuments';
const corporateDocsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/documents/corporate',
  component: CorporateDocuments,
});
// C-5b — global cross-module record search (/search)
import { GlobalSearch } from './screens/GlobalSearch';
const globalSearchRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/search',
  component: GlobalSearch,
});
// DMS-12 — Master Document Dashboard (/documents/dashboard). Literal segment, so
// it resolves before the /documents/$id detail route.
import { DocumentDashboard } from './screens/DocumentDashboard';
const documentDashboardRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/documents/dashboard',
  component: DocumentDashboard,
});
// DMS-13 — periodic review queue (/documents/periodic-reviews)
import { PeriodicReviewDashboard } from './screens/PeriodicReviewDashboard';
const documentPeriodicReviewsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/documents/periodic-reviews',
  component: PeriodicReviewDashboard,
});
// Archive & Records module (drafts/57 P3). Literal /archive/records resolves
// before the /archive/records/$id detail route.
import { ArchiveDashboard, ArchiveRecords, ArchiveRecordDetail } from './screens/Archive';
const archiveDashboardRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/archive',
  component: ArchiveDashboard,
});
const archiveRecordsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/archive/records',
  // Drill-down filters arrive as ?status / ?legal_hold / ?record_type from the
  // Archive dashboard stat-cards; ArchiveRecords reads them via useSearch.
  validateSearch: (search: Record<string, unknown>): { status?: string; legal_hold?: string; record_type?: string } => ({
    status: typeof search['status'] === 'string' ? search['status'] : undefined,
    legal_hold: typeof search['legal_hold'] === 'string' ? search['legal_hold'] : undefined,
    record_type: typeof search['record_type'] === 'string' ? search['record_type'] : undefined,
  }),
  component: ArchiveRecords,
});
function ArchiveRecordDetailRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <ArchiveRecordDetail recordId={id} />; }
const archiveRecordDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/archive/records/$id',
  component: ArchiveRecordDetailRoute,
});

// eDMS E3 — redline accept/reject review (/documents/$id/redline)
import { DocumentRedline } from './screens/DocumentRedline';
const documentRedlineRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/documents/$id/redline',
  component: DocumentRedline,
});
// UX hardening — in-app document reader (/documents/$id/read)
import { DocumentReader } from './screens/DocumentReader';
const documentReaderRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/documents/$id/read',
  component: DocumentReader,
});

// v0.12.5 — HomeDashboardV1 backup at /home-v1 (in case the v0.12.5
// redesign at / isn't preferred).
import { HomeDashboardV1 } from './screens/Home/HomeDashboardV1';
const homeV1Route = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/home-v1',
  component: HomeDashboardV1,
});

// v0.12.9 — BMR audit-chain replay (AI-310). Inspector-mode timeline of
// every AI review + reviewer verdict on one BMR execution.
import { BmrReplay } from './screens/BmrReplay';
function BmrReplayRoute() { return <BmrReplay />; }
const bmrReplayRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/inspector/bmr-replay/$id',
  component: BmrReplayRoute,
});

// drafts/53 §C — In-app DOCX editor (OnlyOffice) — extracts $id from path
function DocumentEditorRoute() {
  const { id } = useParams({ strict: false }) as { id: string };
  return <DocumentEditor documentId={id} />;
}
const documentEditorRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/documents/$id/edit',
  component: DocumentEditorRoute,
});

// Phase 4 — training tasks (the Reader's training inbox)
const trainingTasksRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/training',
  component: TrainingTasks,
});

// Phase 4.5 — OCR review queue (Doc Controller)
const ocrReviewRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/ocr/review',
  component: OcrReviewQueue,
});

// ---------------------------------------------------------------------------
// Phase 6 screens — wired 2026-05-18 (Phase 6 closure).
//
// RBAC note: this router has no route-level RBAC gate; the auth guard only
// enforces "is the user authenticated?". Per-role access is enforced inside
// each screen component (and on the back-end). The `TODO RBAC` comments
// below capture the role allow-lists from the Phase 6 spec so a future
// `requireAnyRole(...)` helper can be wired up in one place.
// ---------------------------------------------------------------------------

// Phase 6a — Retention
// M4 — route-level RBAC role groups. Supersets of the sidebar leaf roles so a
// visible link is always reachable; super-users bypass (see RequireRoles).
const RBAC_RETENTION = ['QAManager', 'DocController'];
const RBAC_QM = ['QualityAnalyticsViewer', 'QADirector', 'SiteQualityHead', 'QAManager'];
const RBAC_QM_THRESHOLDS = ['QAManager', 'QADirector'];
const RBAC_QM_SNAPSHOTS = ['QADirector', 'QAManager'];
const RBAC_CHANGE = ['Author', 'QAManager', 'QAApprover'];
const RBAC_SUPPLIER = ['ProcurementOfficer', 'SupplierManager', 'QAManager', 'QAApprover', 'QADirector', 'AuditorInternal'];
const RBAC_VENDOR_EVAL = ['QAApprover', 'QAManager'];
const RBAC_EQUIPMENT = ['ValidationLead', 'CalibrationTechnician', 'MaintenanceEngineer', 'QAManager'];

// TODO RBAC /retention/matrix      : QAManager + DocController
const retentionMatrixRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/retention/matrix',
  component: gated(RetentionMatrixAdmin, RBAC_RETENTION),
});

// TODO RBAC /retention/disposition : QAManager + DocController
const retentionDispositionRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/retention/disposition',
  component: gated(RecordsDispositionTray, RBAC_RETENTION),
});

// Phase 6b — Quality Metrics v2
// TODO RBAC /quality-metrics            : QualityAnalyticsViewer + QADirector + SiteQualityHead
const qualityMetricsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/quality-metrics',
  component: gated(QualityMetricsDashboard, RBAC_QM),
});

// TODO RBAC /quality-metrics/thresholds : QAManager
const qualityMetricsThresholdsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/quality-metrics/thresholds',
  component: gated(ThresholdMatrixAdmin, RBAC_QM_THRESHOLDS),
});

// TODO RBAC /quality-metrics/snapshots  : QADirector
const qualityMetricsSnapshotsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/quality-metrics/snapshots',
  component: gated(SnapshotSigningTray, RBAC_QM_SNAPSHOTS),
});

// Phase 6c — CC Impact Engine
// Change Control register (Quality Events) — the list/queue of change requests.
const changeRegisterRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/changes',
  component: LegacyChangeRegister,
});
// TODO RBAC /changes/new                : Author (propose); view = Author + QAManager + QAApprover
const changeProposalRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/changes/new',
  component: gated(ChangeRequestProposal, RBAC_CHANGE),
});

// Wrappers pass the real :id from the path so the screens don't fall back to
// the 'cc-demo' default (drafts/61 F1-1).
function ImpactSetReviewRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <ImpactSetReview changeId={id} />; }
function PostActionTrackerRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <PostActionTracker changeId={id} />; }

// TODO RBAC /changes/$id/impact-set     : Author + QAManager + QAApprover (view)
const changeImpactSetRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/changes/$id/impact-set',
  component: gated(ImpactSetReviewRoute, RBAC_CHANGE),
});

// TODO RBAC /changes/$id/post-actions   : Author + QAManager + QAApprover (view)
const changePostActionsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/changes/$id/post-actions',
  component: gated(PostActionTrackerRoute, RBAC_CHANGE),
});

// Phase 6d — Supplier Management
// TODO RBAC /suppliers/*       : ProcurementOfficer + SupplierManager + QAManager + QAApprover
const suppliersRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/suppliers',
  component: gated(SupplierWorkbench, RBAC_SUPPLIER),
});

// Sub-routes for /suppliers/risk-ranking and /suppliers/kpi-dashboard must be
// declared BEFORE the /:id detail route so TanStack Router resolves the
// literal segments first instead of treating them as ids.
const suppliersRiskRankingRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/suppliers/risk-ranking',
  component: gated(RiskRankingDashboard, RBAC_SUPPLIER),
});

const suppliersKpiDashboardRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/suppliers/kpi-dashboard',
  component: gated(SupplierKpiDashboard, RBAC_SUPPLIER),
});

// Supplier S1 — Approved Vendor List (material ↔ supplier matrix)
import { ApprovedVendorList } from './screens/ApprovedVendorList';
const suppliersAvlRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/suppliers/avl',
  component: gated(ApprovedVendorList, RBAC_SUPPLIER),
});
// Supplier S4 — audit findings library
import { SupplierAuditFindings } from './screens/SupplierAuditFindings';
const suppliersAuditFindingsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/suppliers/audit-findings',
  component: gated(SupplierAuditFindings, RBAC_SUPPLIER),
});
// Supplier S3 — change-notification workflow
import { SupplierChangeNotifications } from './screens/SupplierChangeNotifications';
const suppliersScnRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/suppliers/change-notifications',
  component: gated(SupplierChangeNotifications, RBAC_SUPPLIER),
});

// Pass the $id route param through (the component otherwise falls back to its
// 'sup-demo' default, which isn't seeded → 404). Mirrors DocumentEditorRoute.
function SupplierDetailRoute() {
  const { id } = useParams({ strict: false }) as { id: string };
  return <SupplierDetail supplierId={id} />;
}
const supplierDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/suppliers/$id',
  component: gated(SupplierDetailRoute, RBAC_SUPPLIER),
});
// Supplier S2 — supplier self-service portal view (/suppliers/$id/portal)
import { SupplierPortal } from './screens/SupplierPortal';
const supplierPortalRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/suppliers/$id/portal',
  component: gated(SupplierPortal, RBAC_SUPPLIER),
});

// TODO RBAC /vendor-evaluations/$id : QAApprover + QAManager
// Pass the $id route param through (previously the component fell back to its
// 'eval-demo' default, which isn't seeded → 404; real evaluations are eval-001/002).
function VendorEvaluationRoute() {
  const { id } = useParams({ strict: false }) as { id: string };
  return <VendorEvaluationScorecard evaluationId={id} />;
}
const vendorEvaluationRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/vendor-evaluations/$id',
  component: gated(VendorEvaluationRoute, RBAC_VENDOR_EVAL),
});

// Phase 6e — Equipment & Validation
// TODO RBAC /equipment/* : ValidationLead + CalibrationTechnician + MaintenanceEngineer + QAManager
const equipmentRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/equipment',
  component: gated(EquipmentWorkbench, RBAC_EQUIPMENT),
});

// Literal sub-paths declared before /:id so they resolve before the dynamic
// detail route (same ordering rule as /suppliers/* above).
const equipmentCalibrationsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/equipment/calibrations',
  component: gated(CalibrationTray, RBAC_EQUIPMENT),
});

const equipmentPmRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/equipment/pm',
  component: gated(PMTray, RBAC_EQUIPMENT),
});

const equipmentRequalRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/equipment/requalification-schedule',
  component: gated(RequalificationDashboard, RBAC_EQUIPMENT),
});

const equipmentDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/equipment/$id',
  component: gated(EquipmentDetailRoute, RBAC_EQUIPMENT),
});

// ---------------------------------------------------------------------------
// Phase 7 — Full eQMS modules (CAPA / Deviation / OOS / Risk / Internal Audit /
// Management Review). All routes auth-required; per-role RBAC enforced inside
// each component + on the backend. Literal sub-paths registered before $id.
// ---------------------------------------------------------------------------

// CAPA
const capaWorkbenchRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/capa',
  component: CapaWorkbench,
});
const capaDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/capa/$id',
  component: CapaDetailRoute,
});

// Deviation
const deviationsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/deviations',
  component: DeviationTriage,
});
const deviationDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/deviations/$id',
  component: DeviationDetailRoute,
});

// OOS
const oosListRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/oos',
  component: OosLog,
});
const oosDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/oos/$id',
  component: OosDetailRoute,
});

// Risk
const risksRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/risks',
  component: RiskRegister,
});
const riskDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/risks/$id',
  component: RiskEntryRoute,
});

// Complaints (C-2)
const complaintsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/complaints',
  component: ComplaintsRegister,
});
const complaintDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/complaints/$id',
  component: ComplaintDetailRoute,
});

// Recalls (C-3)
const recallsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/recalls',
  component: RecallsRegister,
});
// Change Control — new change_control module (real Spectropharma CRs)
const changeControlRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/change-control',
  component: ChangeControlRegister,
});
// CC-14 — static dashboard route (registered before $id so it wins over the
// dynamic detail route).
const changeControlDashboardRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/change-control/dashboard',
  component: ChangeControlDashboard,
});
// PROD-1 — production master data + artwork register
const productionRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/production',
  component: ProductionMaster,
});
// PV-1 — pharmacovigilance product portfolio + tracker registers
const pharmacovigilanceRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/pharmacovigilance',
  component: PharmacovigilanceRegister,
});
const changeControlDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/change-control/$id',
  component: ChangeControlDetailRoute,
});
const recallDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/recalls/$id',
  component: RecallDetailRoute,
});
// C-7 — Validation / Qualification events
const validationRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/validation',
  component: ValidationRegister,
});
const validationDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/validation/$id',
  component: ValidationDetailRoute,
});
// C-8 — Supplier SCAR (Corrective Action Request)
const scarsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/scars',
  component: ScarRegister,
});
const scarDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/scars/$id',
  component: ScarDetailRoute,
});

// Regulatory Affairs / RA-PLM (C-1) — dashboard + Master Product Register
const regulatoryDashboardRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/regulatory/dashboard',
  component: RaDashboard,
});
const regulatoryProductsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/regulatory/products',
  component: ProductRegister,
});
const regulatoryProductDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/regulatory/products/$id',
  component: ProductDetailRoute,
});
const regulatoryRegistrationsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/regulatory/registrations',
  component: RegistrationTracker,
});
const regulatoryRegistrationDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/regulatory/registrations/$id',
  component: RegistrationDetailRoute,
});
const regulatoryVariationsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/regulatory/variations',
  component: VariationManagement,
});
const regulatoryVariationDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/regulatory/variations/$id',
  component: VariationDetailRoute,
});
// C-1e — Renewals tracker + Country/Market matrix
const regulatoryRenewalsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/regulatory/renewals',
  component: RenewalsTracker,
});
const regulatoryMarketMatrixRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/regulatory/market-matrix',
  component: CountryMatrix,
});
// C-1f — post-approval commitments
const regulatoryCommitmentsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/regulatory/commitments',
  component: CommitmentsTracker,
});
function CommitmentDetailRoute() { const { id } = useParams({ strict: false }) as { id: string }; return <CommitmentDetail commitmentId={id} />; }
const regulatoryCommitmentDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/regulatory/commitments/$id',
  component: CommitmentDetailRoute,
});

// Internal Audit
const internalAuditsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/internal-audits',
  component: AuditCalendar,
});
// Alias /audits → AuditCalendar (the URL the side-nav surfaces; introduced
// in v0.10 walkthrough after the original /audits path 404'd).
const auditsAliasRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/audits',
  component: AuditCalendar,
});
const internalAuditDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/internal-audits/$id',
  component: AuditDetailRoute,
});

// ---------------------------------------------------------------------------
// v0.13 — Finance module (Tier 1: COQ + Risk-to-Cash)
// All routes role-gated inside FinanceLayout (FinanceReader / Editor / CFO /
// ExecutiveSponsor). Non-finance users see a "Restricted" panel.
// ---------------------------------------------------------------------------

const financeExecutiveRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/finance/executive',
  component: ExecutiveDashboard,
});
const financeWorkingCapitalRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/finance/working-capital',
  component: WorkingCapitalCenter,
});
const financeTreasuryRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/finance/treasury',
  component: TreasuryCenter,
});
const financeProfitabilityRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/finance/profitability',
  component: ProfitabilityCenter,
});
const financePharmaKpisRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/finance/pharma-kpis',
  component: PharmaKpiCenter,
});
const financeBoardMetricsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/finance/board-metrics',
  component: BoardMetrics,
});
const financeForecastingRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/finance/forecasting',
  component: ForecastingCenter,
});
const financeUploadsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/finance/uploads',
  component: FinanceUploads,
});
const financeCockpitRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/cockpit',
  component: QualityFinanceCockpit,
});
const financeCostOfQualityRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality',
  component: CostOfQualityDashboard,
});
const financeCostAttributionRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/cost-attribution',
  component: CostEntriesPage,
});
const financeExposureRegisterRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/exposure-register',
  component: ExposureRegister,
});
const financeBoardPackRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/board-pack',
  component: BoardPack,
});
const financeCostRatesAdminRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/admin/cost-rates',
  component: CostRatesAdmin,
});
const financePeriodLocksAdminRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/admin/period-locks',
  component: PeriodLocksAdmin,
});

// v0.13.2 — Finance Tier 2 (Budgets + Variance)
const financeBudgetsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/budgets',
  component: Budgets,
});
const financeVarianceRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/variance',
  component: Variance,
});

// v0.13.3 — Finance Tier 3 (CAPEX)
const financeCapexRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/capex',
  component: CapexPortfolio,
});
const financeCapexDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/capex/$id',
  component: CapexDetail,
});
// Finance Phase 1a — CAPA ROI ranking
const financeCapaRoiRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/capa-roi',
  component: CapaRoiRanking,
});
// Finance F4 — automated cost capture
const financeCostCaptureRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/cost-capture',
  component: CostCapture,
});
// Finance F5 — ERP write-back + period close
const financePeriodCloseRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/period-close',
  component: PeriodClose,
});
// Finance F6 — board-grade analytics
const financeBoardAnalyticsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/board-analytics',
  component: BoardAnalytics,
});

// v0.13.4 — Finance Tier 4a (ERP read-only)
const financeErpReconRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/erp-reconciliation',
  component: ErpReconciliation,
});
const financeErpAdminRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/admin/erp',
  component: ErpAdmin,
});
// W5.4
const financeAuditRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/cost-of-quality/admin/audit',
  component: FinanceAuditViewer,
});

// ── Legacy /finance/* → /cost-of-quality/* redirects ───────────────────────
// The Cost-of-Quality module split out of Corporate Finance to its own URL
// prefix. These thin redirect routes keep old bookmarks / deep links working.
const COQ_LEGACY_REDIRECTS: Array<[string, string]> = [
  ['/finance/cost-of-quality', '/cost-of-quality'],
  ['/finance/cockpit', '/cost-of-quality/cockpit'],
  ['/finance/cost-attribution', '/cost-of-quality/cost-attribution'],
  ['/finance/cost-capture', '/cost-of-quality/cost-capture'],
  ['/finance/exposure-register', '/cost-of-quality/exposure-register'],
  ['/finance/budgets', '/cost-of-quality/budgets'],
  ['/finance/variance', '/cost-of-quality/variance'],
  ['/finance/capex', '/cost-of-quality/capex'],
  ['/finance/capa-roi', '/cost-of-quality/capa-roi'],
  ['/finance/erp-reconciliation', '/cost-of-quality/erp-reconciliation'],
  ['/finance/period-close', '/cost-of-quality/period-close'],
  ['/finance/board-pack', '/cost-of-quality/board-pack'],
  ['/finance/board-analytics', '/cost-of-quality/board-analytics'],
  ['/finance/admin/cost-rates', '/cost-of-quality/admin/cost-rates'],
  ['/finance/admin/period-locks', '/cost-of-quality/admin/period-locks'],
  ['/finance/admin/erp', '/cost-of-quality/admin/erp'],
  ['/finance/admin/audit', '/cost-of-quality/admin/audit'],
];
const coqLegacyRedirectRoutes = COQ_LEGACY_REDIRECTS.map(([from, to]) =>
  createRoute({
    getParentRoute: () => authedLayoutRoute,
    path: from,
    beforeLoad: () => { throw redirect({ to: to as never }); },
    component: () => null,
  }),
);
const coqLegacyCapexDetailRedirect = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/finance/capex/$id',
  beforeLoad: ({ params }) => { throw redirect({ to: '/cost-of-quality/capex/$id', params }); },
  component: () => null,
});

// Management Review
const managementReviewsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/management-reviews',
  component: ReviewCalendar,
});
const managementReviewDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/management-reviews/$id',
  component: ReviewMinutesRoute,
});

// ----- Assemble the route tree -----
const routeTree = rootRoute.addChildren([
  loginRoute,
  authCallbackRoute,
  twoFactorRoute,
  authedLayoutRoute.addChildren([
    homeRoute,
    actionCenterRoute,
    workbenchRoute,
    workflowDetailRoute,
    qaRoute,
    ruReaderRoute,
    inspectorRoute,
    notificationsRoute,
    webhookAdminRoute,
    tenantConfigAdminRoute,
    documentNumberingRoute,
    dataScenariosRoute,
    bmrReviewRoute,
    qualityIndexRoute,
    bmrReviewIndexRoute,
    departmentAdminRoute,
    sitesAdminRoute,
    templateLibraryRoute,
    qualityDashboardRoute,
    documentDashboardRoute,
    documentPeriodicReviewsRoute,
    archiveDashboardRoute,
    archiveRecordsRoute,
    archiveRecordDetailRoute,
    documentDetailRoute,
    documentSearchRoute,
    ocrSearchRoute,
    corporateDocsRoute,
    globalSearchRoute,
    documentRedlineRoute,
    documentReaderRoute,
    homeV1Route,
    bmrReplayRoute,
    documentEditorRoute,
    trainingTasksRoute,
    ocrReviewRoute,
    // Phase 6a — Retention
    retentionMatrixRoute,
    retentionDispositionRoute,
    // Phase 6b — Quality Metrics v2
    qualityMetricsRoute,
    qualityMetricsThresholdsRoute,
    qualityMetricsSnapshotsRoute,
    // Phase 6c — CC Impact Engine
    changeRegisterRoute,
    changeProposalRoute,
    changeImpactSetRoute,
    changePostActionsRoute,
    // Phase 6d — Supplier Management
    // Literal sub-paths registered before the dynamic $id route.
    suppliersRiskRankingRoute,
    suppliersKpiDashboardRoute,
    suppliersAvlRoute,
    suppliersAuditFindingsRoute,
    suppliersScnRoute,
    suppliersRoute,
    supplierDetailRoute,
    supplierPortalRoute,
    vendorEvaluationRoute,
    // Phase 6e — Equipment & Validation
    // Literal sub-paths registered before the dynamic $id route.
    equipmentCalibrationsRoute,
    equipmentPmRoute,
    equipmentRequalRoute,
    equipmentRoute,
    equipmentDetailRoute,
    // Phase 7 — Full eQMS
    capaWorkbenchRoute,
    capaDetailRoute,
    deviationsRoute,
    deviationDetailRoute,
    oosListRoute,
    oosDetailRoute,
    risksRoute,
    riskDetailRoute,
    complaintsRoute,
    complaintDetailRoute,
    recallsRoute,
    changeControlRoute,
    changeControlDashboardRoute,
    changeControlDetailRoute,
    productionRoute,
    pharmacovigilanceRoute,
    recallDetailRoute,
    validationRoute,
    validationDetailRoute,
    scarsRoute,
    scarDetailRoute,
    regulatoryDashboardRoute,
    regulatoryProductsRoute,
    regulatoryProductDetailRoute,
    regulatoryRegistrationsRoute,
    regulatoryRegistrationDetailRoute,
    regulatoryVariationsRoute,
    regulatoryVariationDetailRoute,
    regulatoryRenewalsRoute,
    regulatoryMarketMatrixRoute,
    regulatoryCommitmentsRoute,
    regulatoryCommitmentDetailRoute,
    internalAuditsRoute,
    auditsAliasRoute,
    internalAuditDetailRoute,
    managementReviewsRoute,
    managementReviewDetailRoute,
    // v0.13 — Finance module (Tier 1)
    financeExecutiveRoute,
    financeWorkingCapitalRoute,
    financeTreasuryRoute,
    financeProfitabilityRoute,
    financePharmaKpisRoute,
    financeBoardMetricsRoute,
    financeForecastingRoute,
    financeUploadsRoute,
    financeCockpitRoute,
    financeCostOfQualityRoute,
    financeCostAttributionRoute,
    financeExposureRegisterRoute,
    financeBoardPackRoute,
    financeCostRatesAdminRoute,
    financePeriodLocksAdminRoute,
    // v0.13.2 — Finance Tier 2
    financeBudgetsRoute,
    financeVarianceRoute,
    // v0.13.3 — Finance Tier 3 (CAPEX). Detail before list isn't required —
    // dynamic $id is at a separate path level, so no ordering conflict.
    financeCapexRoute,
    financeCapexDetailRoute,
    financeCapaRoiRoute,
    financeCostCaptureRoute,
    financePeriodCloseRoute,
    financeBoardAnalyticsRoute,
    // v0.13.4 — Finance Tier 4a (ERP read-only)
    financeErpReconRoute,
    financeErpAdminRoute,
    // W5.4 — Finance audit log viewer
    financeAuditRoute,
    // Legacy /finance/* → /cost-of-quality/* redirects (NAV split)
    ...coqLegacyRedirectRoutes,
    coqLegacyCapexDetailRedirect,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultStaleTime: 30_000,
});

// Module augmentation for TanStack Router type-safety of `to:` literals.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function AppRouter() {
  // useSession is the render-time source of truth; rendering the provider
  // happens at RootLayout, but we expose the hook for downstream screens.
  // Touching it here prevents tree-shaking from dropping the import.
  void useSession;
  return <RouterProvider router={router} />;
}
